import { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, Save, RotateCcw, ZoomIn, ZoomOut, Contrast, Sun, Search, User, AlertCircle, CheckCircle, Loader2, Bug, CheckSquare, Square, Play, Pause, SkipForward } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import toast from 'react-hot-toast';
import { detectFaceWithMediapipe, resetMediapipeDetector } from '../lib/mediapipeFaceDetection';

interface Student {
  id: number;
  sophomorixUnid: string;
  name: string;
  vorname: string;
  klasse: string;
  fullName: string;
  hasPhoto: boolean;
  photoPreview?: string;
}

interface StudentPhoto extends Student {
  photo: string;
}

interface PhotoStats {
  totalStudents: number;
  studentsWithPhotos: number;
  studentsWithoutPhotos: number;
  photoPercentage: number;
}

type FaceDetectionBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type HeatmapWindow = {
  x: number;
  y: number;
  width: number;
  height: number;
  score: number;
};

type FaceDetectorInstance = {
  detect: (image: ImageBitmapSource) => Promise<Array<{ boundingBox?: FaceDetectionBox | DOMRectReadOnly }>>;
};

export default function FotoOptimierung() {
  const { user } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<StudentPhoto | null>(null);
  const [stats, setStats] = useState<PhotoStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Canvas und Bildverarbeitung Refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const originalImageRef = useRef<HTMLImageElement | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Bildverarbeitungs-Parameter
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [faceDetected, setFaceDetected] = useState(false);
  const [faceBounds, setFaceBounds] = useState<FaceDetectionBox | null>(null);
  const faceDetectorRef = useRef<FaceDetectorInstance | null>(null);
  const faceDetectionRequestRef = useRef(0);
  const animationFrameRef = useRef<number | null>(null);
  const faceBoundsRef = useRef<FaceDetectionBox | null>(null);
  const renderInfoRef = useRef<{
    offsetX: number;
    offsetY: number;
    drawWidth: number;
    drawHeight: number;
    sourceX: number;
    sourceY: number;
    sourceWidth: number;
    sourceHeight: number;
  } | null>(null);
  const [cropRegion, setCropRegion] = useState<{ x: number; y: number; size: number } | null>(null);
  const cropRegionRef = useRef<{ x: number; y: number; size: number } | null>(null);
  const [showDebugOverlay, setShowDebugOverlay] = useState(false);
  const [heatmapData, setHeatmapData] = useState<HeatmapWindow[]>([]);
  const [lastDetectionMethod, setLastDetectionMethod] = useState<'faceDetector' | 'mediapipe' | 'fallback' | 'none' | null>(null);
  const [lastFallbackScore, setLastFallbackScore] = useState<number | null>(null);

  // Batch-Modus States
  const [batchMode, setBatchMode] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [batchQueue, setBatchQueue] = useState<StudentPhoto[]>([]);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchPaused, setBatchPaused] = useState(false);
  const [batchResults, setBatchResults] = useState<{success: number, errors: number, processed: number}>({success: 0, errors: 0, processed: 0});
  const [fullAutoMode, setFullAutoMode] = useState(false);
  const [autoProcessingTimeout, setAutoProcessingTimeout] = useState<NodeJS.Timeout | null>(null);
  const batchQueueRef = useRef<StudentPhoto[]>([]);
  const currentBatchIndexRef = useRef(0);
  const fullAutoModeRef = useRef(false);
  const imageLoadedRef = useRef(false);
  const fullAutoProcessingRef = useRef<Promise<void> | null>(null);
  const selectedStudentRef = useRef<StudentPhoto | null>(null);

  const updateSelectedStudent = useCallback((student: StudentPhoto | null | ((prev: StudentPhoto | null) => StudentPhoto | null)) => {
    const newValue = typeof student === 'function' ? (student as (prev: StudentPhoto | null) => StudentPhoto | null)(selectedStudentRef.current) : student;
    selectedStudentRef.current = newValue;
    setSelectedStudent(newValue);
  }, []);

  // Prüfe Admin-Berechtigung
  const isAdmin = user?.role === 'admin' || user?.username === 'admin';

  useEffect(() => {
    if (!isAdmin) {
      setError('Nur Administratoren können auf die Foto-Optimierung zugreifen');
      return;
    }
    loadData();
  }, [isAdmin]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if ('FaceDetector' in window) {
      try {
        faceDetectorRef.current = new (window as any).FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
      } catch (detectorError) {
        console.warn('FaceDetector konnte nicht initialisiert werden:', detectorError);
        faceDetectorRef.current = null;
      }
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (autoProcessingTimeout) {
        clearTimeout(autoProcessingTimeout);
      }
    };
  }, []);

  useEffect(() => {
    faceBoundsRef.current = faceBounds;
  }, [faceBounds]);

  useEffect(() => {
    cropRegionRef.current = cropRegion;
  }, [cropRegion]);

  useEffect(() => {
    selectedStudentRef.current = selectedStudent;
  }, [selectedStudent]);

  useEffect(() => {
    batchQueueRef.current = batchQueue;
  }, [batchQueue]);

  useEffect(() => {
    currentBatchIndexRef.current = currentBatchIndex;
  }, [currentBatchIndex]);

  useEffect(() => {
    fullAutoModeRef.current = fullAutoMode;
  }, [fullAutoMode]);

  useEffect(() => {
    imageLoadedRef.current = imageLoaded;
  }, [imageLoaded]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [studentsResponse, statsResponse] = await Promise.all([
        api.get('/foto-optimierung/students'),
        api.get('/foto-optimierung/stats')
      ]);

      setStudents(studentsResponse.data.data);
      setStats(statsResponse.data.data);
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const loadStudentPhoto = async (sophomorixUnid: string) => {
    try {
      console.log('[FotoOptimierung] Lade Schülerfoto', { sophomorixUnid });
      setImageLoaded(false);
      setFaceDetected(false);
      setFaceBounds(null);
      setCropRegion(null);
      cropRegionRef.current = null;
      setBrightness(0);
      setContrast(0);
      setRotation(0);
      setHeatmapData([]);
      setLastDetectionMethod(null);
      setLastFallbackScore(null);
      resetMediapipeDetector();

      const response = await api.get(`/foto-optimierung/student/${sophomorixUnid}`);
      const studentPhoto = response.data.data;
      console.log('[FotoOptimierung] API-Antwort für Schülerfoto', {
        hasPhoto: Boolean(studentPhoto?.photo),
        fullName: studentPhoto?.fullName,
        klasse: studentPhoto?.klasse,
      });
      
      if (!studentPhoto) {
        toast.error('Keine Schülerdaten erhalten');
        return;
      }
      
      if (!studentPhoto.photo) {
        toast.error('Kein Foto für diesen Schüler gefunden');
        return;
      }

      updateSelectedStudent(studentPhoto);
      
      try {
        await loadImageForProcessing(studentPhoto.photo);
        console.log('[FotoOptimierung] Bild erfolgreich geladen und vorbereitet');
      } catch (imageError) {
        console.error('Fehler beim Laden des Bildes:', imageError);
        toast.error('Fehler beim Laden des Bildes');
      }
    } catch (error) {
      console.error('Fehler beim Laden des Schülerfotos:', error);
      toast.error('Fehler beim Laden des Schülerfotos');
    }
  };

  const loadImageForProcessing = async (photoDataUrl: string | null) => {
    return new Promise<void>((resolve, reject) => {
      if (!photoDataUrl) {
        reject(new Error('Kein Foto verfügbar'));
        return;
      }

      const trimmed = photoDataUrl.trim();
      const hasPrefix = trimmed.startsWith('data:image/');
      const normalizeDataUrl = (value: string) => {
        if (!value.startsWith('data:image/')) {
          return `data:image/jpeg;base64,${value.replace(/\s+/g, '')}`;
        }
        const commaIndex = value.indexOf(',');
        if (commaIndex === -1) {
          return value;
        }
        const prefix = value.slice(0, commaIndex + 1);
        const base64Part = value.slice(commaIndex + 1).replace(/\s+/g, '');
        return prefix + base64Part;
      };

      const payload = normalizeDataUrl(trimmed);
      const img = new Image();
      img.decoding = 'async';

      const decodeBase64 = (data: string) => {
        if (typeof window === 'undefined' || typeof window.atob !== 'function') {
          return null;
        }
        try {
          return window.atob(data);
        } catch (err) {
          console.warn('[FotoOptimierung] Base64-Dekodierung fehlgeschlagen', err);
          return null;
        }
      };

      const tryLoad = (source: string, allowRetry: boolean) => {
        if (!source.startsWith('data:')) {
          img.crossOrigin = 'anonymous';
        }

        img.onload = () => {
          originalImageRef.current = img;
          setImageLoaded(true);
          imageLoadedRef.current = true;
          console.log('[FotoOptimierung] Bild erfolgreich geladen:', {
            width: img.width,
            height: img.height,
            naturalWidth: img.naturalWidth,
            naturalHeight: img.naturalHeight
          });
          resolve();
        };

        img.onerror = (error) => {
          if (allowRetry && source.startsWith('data:image/')) {
            const commaIndex = source.indexOf(',');
            if (commaIndex !== -1) {
              const prefix = source.slice(0, commaIndex + 1);
              const base64Part = source.slice(commaIndex + 1).replace(/\s+/g, '');
              const decoded = decodeBase64(base64Part);

              if (decoded) {
                const hasHighBytes = [...decoded.slice(0, 8)].some(char => char.charCodeAt(0) > 127 || char.charCodeAt(0) === 0);
                const looksAsciiBase64 = /^[A-Za-z0-9+/=\s]+$/.test(decoded.slice(0, 80));

                if (!hasHighBytes && looksAsciiBase64) {
                  console.warn('[FotoOptimierung] Erneuter Ladeversuch nach doppelter Base64-Erkennung');
                  const reencoded = decoded.replace(/\s+/g, '');
                  tryLoad(prefix + reencoded, false);
                  return;
                }
              }
            }
          }

          console.error('[FotoOptimierung] Fehler beim Laden des Bildes:', {
            error,
            src: source.substring(0, 120) + (source.length > 120 ? '…' : ''),
            srcLength: source.length,
            hasPrefix: source.startsWith('data:image/')
          });
          reject(new Error('Bild konnte nicht geladen werden'));
        };

        img.src = source;
      };

      tryLoad(payload, true);
    });
  };

  const normalizeBoundingBox = useCallback((box: FaceDetectionBox | DOMRectReadOnly | undefined | null): FaceDetectionBox | null => {
    if (!box) {
      return null;
    }

    const x = 'x' in box ? box.x : ('left' in (box as DOMRectReadOnly) ? (box as DOMRectReadOnly).left : 0);
    const y = 'y' in box ? box.y : ('top' in (box as DOMRectReadOnly) ? (box as DOMRectReadOnly).top : 0);
    const width = 'width' in box ? box.width : 0;
    const height = 'height' in box ? box.height : 0;

    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !Number.isFinite(height)) {
      return null;
    }

    return { x, y, width, height };
  }, []);

  const renderImage = useCallback(() => {
    if (!originalImageRef.current || !canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = originalImageRef.current;

    if (!ctx) {
      return;
    }

    canvas.width = 400;
    canvas.height = 400;

    const crop = cropRegionRef.current;
    let sourceX = 0;
    let sourceY = 0;
    let sourceWidth = img.width;
    let sourceHeight = img.height;

    if (crop) {
      sourceX = crop.x;
      sourceY = crop.y;
      sourceWidth = crop.size;
      sourceHeight = crop.size;
    }

    const maxSize = Math.min(canvas.width, canvas.height);
    const imgAspect = sourceWidth / sourceHeight;
    const canvasAspect = 1;

    let drawWidth = canvas.width;
    let drawHeight = canvas.height;
    let offsetX = 0;
    let offsetY = 0;

    if (!crop) {
      drawWidth = maxSize;
      drawHeight = maxSize;

      if (imgAspect > canvasAspect) {
        drawHeight = maxSize;
        drawWidth = drawHeight * imgAspect;
        offsetX = (canvas.width - drawWidth) / 2;
      } else {
        drawWidth = maxSize;
        drawHeight = drawWidth / imgAspect;
        offsetY = (canvas.height - drawHeight) / 2;
      }
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();

    if (rotation !== 0) {
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-canvas.width / 2, -canvas.height / 2);
    }

    ctx.drawImage(
      img,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      offsetX,
      offsetY,
      drawWidth,
      drawHeight
    );

    renderInfoRef.current = {
      offsetX,
      offsetY,
      drawWidth,
      drawHeight,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
    };

    if (brightness !== 0 || contrast !== 0) {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const contrastFactor = contrast !== 0 ? (259 * (contrast + 255)) / (255 * (259 - contrast)) : 1;

      for (let i = 0; i < data.length; i += 4) {
        let r = data[i];
        let g = data[i + 1];
        let b = data[i + 2];

        if (contrast !== 0) {
          r = Math.max(0, Math.min(255, contrastFactor * (r - 128) + 128));
          g = Math.max(0, Math.min(255, contrastFactor * (g - 128) + 128));
          b = Math.max(0, Math.min(255, contrastFactor * (b - 128) + 128));
        }

        if (brightness !== 0) {
          r = Math.max(0, Math.min(255, r + brightness));
          g = Math.max(0, Math.min(255, g + brightness));
          b = Math.max(0, Math.min(255, b + brightness));
        }

        const saturationBoost = 1.1;
        const gray = 0.299 * r + 0.587 * g + 0.114 * b;
        r = Math.max(0, Math.min(255, gray + saturationBoost * (r - gray)));
        g = Math.max(0, Math.min(255, gray + saturationBoost * (g - gray)));
        b = Math.max(0, Math.min(255, gray + saturationBoost * (b - gray)));

        data[i] = Math.round(r);
        data[i + 1] = Math.round(g);
        data[i + 2] = Math.round(b);
      }

      ctx.putImageData(imageData, 0, 0);
    }

    if (showDebugOverlay && renderInfoRef.current) {
      ctx.save();
      const { offsetX, offsetY, drawWidth, drawHeight, sourceX, sourceY, sourceWidth, sourceHeight } = renderInfoRef.current;

      const mapBoxToCanvas = (box: FaceDetectionBox | HeatmapWindow) => {
        if (sourceWidth <= 0 || sourceHeight <= 0) {
          return null;
        }

        const relativeX = (box.x - sourceX) / sourceWidth;
        const relativeY = (box.y - sourceY) / sourceHeight;
        const relativeWidth = box.width / sourceWidth;
        const relativeHeight = box.height / sourceHeight;

        return {
          x: offsetX + relativeX * drawWidth,
          y: offsetY + relativeY * drawHeight,
          width: relativeWidth * drawWidth,
          height: relativeHeight * drawHeight,
        };
      };

      if (heatmapData.length > 0) {
        heatmapData.forEach((window) => {
          const mapped = mapBoxToCanvas(window);
          if (!mapped) {
            return;
          }

          const clamped = Math.max(0, Math.min(1, window.score));
          const hue = 200 - clamped * 200;
          const alpha = 0.15 + clamped * 0.45;
          ctx.fillStyle = `hsla(${hue}, 90%, 55%, ${alpha})`;
          ctx.fillRect(mapped.x, mapped.y, mapped.width, mapped.height);
        });
      }

      const bounds = faceBoundsRef.current;
      if (bounds) {
        const mapped = mapBoxToCanvas(bounds);
        if (mapped) {
          ctx.strokeStyle = 'rgba(16, 185, 129, 0.9)';
          ctx.lineWidth = 3;
          ctx.strokeRect(mapped.x, mapped.y, mapped.width, mapped.height);
        }
      }

      ctx.restore();
    }

    ctx.restore();
  }, [brightness, contrast, rotation, showDebugOverlay, heatmapData]);

  const processImage = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      renderImage();
      animationFrameRef.current = null;
    });
  }, [renderImage]);

  const waitForNextFrame = useCallback(() => new Promise<void>(resolve => requestAnimationFrame(() => resolve())), []);
  const wait = useCallback((ms: number) => new Promise<void>(resolve => window.setTimeout(resolve, ms)), []);
  const showWarning = useCallback((message: string) => {
    toast(message, { icon: '⚠️' });
  }, []);
  const showInfo = useCallback((message: string) => {
    toast(message, { icon: 'ℹ️' });
  }, []);
  const waitForImageReady = useCallback(async (timeoutMs = 6000) => {
    const start = Date.now();
    while (!imageLoadedRef.current) {
      if (!fullAutoModeRef.current) {
        return false;
      }
      if (Date.now() - start > timeoutMs) {
        return false;
      }
      await wait(120);
    }
    return true;
  }, [wait]);

  const resetProcessingState = useCallback(() => {
    setBrightness(0);
    setContrast(0);
    setRotation(0);
    setCropRegion(null);
    cropRegionRef.current = null;
    setFaceDetected(false);
    setFaceBounds(null);
    faceBoundsRef.current = null;
    originalImageRef.current = null;
    setImageLoaded(false);
    imageLoadedRef.current = false;
    setHeatmapData([]);
    setLastDetectionMethod(null);
    setLastFallbackScore(null);
    resetMediapipeDetector();
  }, []);

  const runFallbackFaceDetection = useCallback((): FaceDetectionBox | null => {
    if (!originalImageRef.current) {
      console.warn('[FotoOptimierung] runFallbackFaceDetection ohne Bild ausgeführt');
      return null;
    }

    const img = originalImageRef.current;
    
    // Verbesserter Fallback: Verwende das Zentrum des Bildes
    // Das ist viel zuverlässiger als die Hautfarbe-Heuristik
    const centerX = img.width / 2;
    const centerY = img.height / 2;
    
    // Gesichtsgröße basierend auf der kleineren Bildseite
    const faceSize = Math.min(img.width, img.height) * 0.6; // 60% der kleineren Seite
    
    const result = {
      x: Math.max(0, centerX - faceSize / 2),
      y: Math.max(0, centerY - faceSize / 2),
      width: Math.min(faceSize, img.width),
      height: Math.min(faceSize, img.height),
    };
    
    console.log('[FotoOptimierung] Verbesserter Fallback - Zentrum verwendet:', {
      imageSize: { width: img.width, height: img.height },
      faceBounds: result,
      faceSize: faceSize
    });
    
    // Setze Heatmap-Daten für Debugging
    setHeatmapData([{
      x: result.x,
      y: result.y,
      width: result.width,
      height: result.height,
      score: 1.0 // Maximaler Score für Zentrum
    }]);
    setLastFallbackScore(1.0);
    
    return result;
  }, []);

  const runFaceDetection = useCallback(async (): Promise<boolean> => {
    if (!originalImageRef.current) {
      console.warn('[FotoOptimierung] runFaceDetection ohne geladenes Bild');
      return false;
    }

    const requestId = ++faceDetectionRequestRef.current;
    let detectionSuccess = false;
    setLastFallbackScore(null);

    const applyBounds = (box: FaceDetectionBox | null) => {
      if (requestId !== faceDetectionRequestRef.current) {
        return false;
      }

      if (box && box.width > 10 && box.height > 10) {
        faceBoundsRef.current = box;
        setFaceBounds(box);
        setFaceDetected(true);
        console.log('[FotoOptimierung] Gesicht erkannt', { box });
        return true;
      }

      setFaceBounds(null);
      setFaceDetected(false);
      faceBoundsRef.current = null;
      console.log('[FotoOptimierung] Kein valides Gesicht im applyBounds');
      return false;
    };

    if (faceDetectorRef.current) {
      try {
        console.log('[FotoOptimierung] Nutze FaceDetector API');
        const results = await faceDetectorRef.current.detect(originalImageRef.current);
        if (requestId === faceDetectionRequestRef.current && Array.isArray(results) && results.length > 0) {
          const normalized = normalizeBoundingBox(results[0].boundingBox);
          if (normalized && applyBounds(normalized)) {
            console.log('[FotoOptimierung] FaceDetector API erfolgreich', { resultsCount: results.length });
            setLastDetectionMethod('faceDetector');
            setHeatmapData([]);
            setLastFallbackScore(null);
            detectionSuccess = true;
            return true;
          }
        }
      } catch (error) {
        if (requestId === faceDetectionRequestRef.current) {
          console.warn('FaceDetector API nicht verfügbar, nutze Fallback:', error);
        }
      }
    }

    if (!detectionSuccess && originalImageRef.current) {
      try {
        console.log('[FotoOptimierung] Versuche Mediapipe FaceDetection');
        const mediapipeBounds = await detectFaceWithMediapipe(originalImageRef.current);
        if (requestId === faceDetectionRequestRef.current) {
          if (mediapipeBounds && applyBounds(mediapipeBounds)) {
            console.log('[FotoOptimierung] Mediapipe-Erkennung erfolgreich');
            setLastDetectionMethod('mediapipe');
            setHeatmapData([]);
            setLastFallbackScore(null);
            detectionSuccess = true;
            return true;
          }
        }
      } catch (mediapipeError) {
        if (requestId === faceDetectionRequestRef.current) {
          console.warn('[FotoOptimierung] Mediapipe-Erkennung fehlgeschlagen', mediapipeError);
        }
      }
    }

    console.log('[FotoOptimierung] Starte Fallback-Erkennung');
    const fallback = runFallbackFaceDetection();
    let fallbackSuccess = false;
    if (fallback) {
      fallbackSuccess = applyBounds(fallback);
    } else {
      applyBounds(null);
    }

    if (fallback && fallbackSuccess) {
      setLastDetectionMethod('fallback');
      detectionSuccess = true;
      console.log('[FotoOptimierung] Fallback-Erkennung erfolgreich');
      return true;
    }

    if (!detectionSuccess) {
      setLastDetectionMethod('none');
      console.log('[FotoOptimierung] Keine Gesichtserkennung möglich');
    }
    return false;
  }, [normalizeBoundingBox, runFallbackFaceDetection]);

  useEffect(() => {
    if (!imageLoaded) {
      return;
    }

    processImage();
  }, [imageLoaded, brightness, contrast, rotation, processImage]);

  useEffect(() => {
    if (!imageLoaded) {
      return;
    }

    processImage();
  }, [cropRegion, imageLoaded, processImage]);

  useEffect(() => {
    if (!imageLoaded) {
      return;
    }

    if (cropRegionRef.current) {
      return;
    }

    if (rotation !== 0) {
      setFaceDetected(false);
      setFaceBounds(null);
      return;
    }

    runFaceDetection();
  }, [imageLoaded, rotation, runFaceDetection]);

  useEffect(() => {
    if (!imageLoaded || rotation !== 0 || cropRegionRef.current) {
      return;
    }

    const timeout = window.setTimeout(() => {
      runFaceDetection();
    }, 250);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [brightness, contrast, imageLoaded, rotation, runFaceDetection]);

  const autoCropToFace = useCallback(async () => {
    if (!originalImageRef.current || !canvasRef.current) {
      return;
    }

    let bounds = faceBoundsRef.current;
    if (!bounds) {
      const detected = await runFaceDetection();
      if (!detected) {
        toast.error('Kein Gesicht erkennbar – bitte manuell anpassen');
        return;
      }
      bounds = faceBoundsRef.current;
      if (!bounds) {
        toast.error('Gesicht konnte nicht lokalisiert werden');
        return;
      }
    }

    const img = originalImageRef.current;

    const padding = 0.4;
    const faceSize = Math.max(bounds.width, bounds.height);
    let cropSize = faceSize * (1 + padding * 2);
    const maxSquare = Math.min(img.width, img.height);
    cropSize = Math.min(Math.max(cropSize, faceSize), maxSquare);

    const faceCenterX = bounds.x + bounds.width / 2;
    const faceCenterY = bounds.y + bounds.height / 2;

    let cropX = faceCenterX - cropSize / 2;
    let cropY = faceCenterY - cropSize / 2;

    cropX = Math.max(0, Math.min(img.width - cropSize, cropX));
    cropY = Math.max(0, Math.min(img.height - cropSize, cropY));

    const region = {
      x: Math.max(0, cropX),
      y: Math.max(0, cropY),
      size: cropSize
    };

    setCropRegion(region);
    cropRegionRef.current = region;
    setFaceBounds(null);
    faceBoundsRef.current = null;
    setFaceDetected(false);
    processImage();
    toast.success('Gesicht fokussiert');
  }, [processImage, runFaceDetection]);

  const autoOptimize = useCallback(async (): Promise<boolean> => {
    if (!originalImageRef.current || !canvasRef.current) {
      toast.error('Kein Bild zum Optimieren geladen');
      return false;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      toast.error('Canvas-Kontext nicht verfügbar');
      return false;
    }

    let imageData: ImageData | null = null;

    try {
      imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    } catch (err) {
      console.warn('Analyse des sichtbaren Canvas nicht möglich, nutze Originalbild', err);
    }

    if (!imageData) {
      const source = originalImageRef.current;
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      tempCanvas.width = source.width;
      tempCanvas.height = source.height;
      tempCtx?.drawImage(source, 0, 0);
      imageData = tempCtx?.getImageData(0, 0, tempCanvas.width, tempCanvas.height) ?? null;
    }

    if (!imageData) {
      toast.error('Bilddaten konnten nicht analysiert werden');
      return false;
    }

    const data = imageData.data;
    if (!data || data.length === 0) {
      toast.error('Keine Bilddaten gefunden');
      return false;
    }

    const histogram = new Array<number>(256).fill(0);
    let totalBrightness = 0;
    const pixelCount = data.length / 4;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const brightness = Math.max(0, Math.min(255, Math.round(0.299 * r + 0.587 * g + 0.114 * b)));
      histogram[brightness] += 1;
      totalBrightness += brightness;
    }

    const getPercentileValue = (percent: number) => {
      const target = percent * pixelCount;
      let cumulative = 0;
      for (let i = 0; i < histogram.length; i++) {
        cumulative += histogram[i];
        if (cumulative >= target) {
          return i;
        }
      }
      return 255;
    };

    const avgBrightness = totalBrightness / (pixelCount || 1);
    const effectiveMin = getPercentileValue(0.05);
    const effectiveMax = getPercentileValue(0.95);
    const contrastRange = Math.max(1, effectiveMax - effectiveMin);

    const clampValue = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

    const targetBrightness = 130;
    let optimalBrightness = clampValue(targetBrightness - avgBrightness, -40, 40);
    if (Math.abs(optimalBrightness) < 1.5) {
      optimalBrightness = 0;
    }

    const targetContrastRange = 160;
    let contrastRatio = targetContrastRange / contrastRange;
    if (!Number.isFinite(contrastRatio) || contrastRatio <= 0) {
      contrastRatio = 1;
    }
    contrastRatio = clampValue(contrastRatio, 0.75, 1.4);
    if (Math.abs(contrastRatio - 1) < 0.05) {
      contrastRatio = 1;
    }

    let optimalContrast = 0;
    if (contrastRatio !== 1) {
      optimalContrast = (259 * 255 * (contrastRatio - 1)) / (255 * contrastRatio + 259);
      optimalContrast = clampValue(optimalContrast, -35, 45);
    }

    setBrightness(Math.round(optimalBrightness));
    setContrast(Math.round(optimalContrast));

    await waitForNextFrame();

    const detected = await runFaceDetection();
    if (detected) {
      await autoCropToFace();
    }

    return true;
  }, [autoCropToFace, runFaceDetection, waitForNextFrame]);

  const resetImage = () => {
    setBrightness(0);
    setContrast(0);
    setRotation(0);
    setCropRegion(null);
    cropRegionRef.current = null;
    faceBoundsRef.current = null;
    setFaceBounds(null);
    setFaceDetected(false);
    processImage();
    runFaceDetection();
  };

  const saveOptimizedPhoto = async () => {
    const student = selectedStudentRef.current;
    if (!student || !canvasRef.current) return;

    setSaving(true);
    const toastId = toast.loading('Speichere optimiertes Foto...');
    
    try {
      console.log('💾 Starte Speichervorgang...');
      const canvas = canvasRef.current;
      
      // Stelle sicher, dass das Canvas korrekt gerendert ist
      await waitForNextFrame();
      await wait(100); // Zusätzliche Pause für Canvas-Rendering
      
      // Optimiere Canvas-Größe für bessere Performance
      const maxSize = 800;
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      
      if (!tempCtx) {
        throw new Error('Temporärer Canvas Context nicht verfügbar');
      }
      
      // Setze optimale Größe
      tempCanvas.width = maxSize;
      tempCanvas.height = maxSize;
      
      // Zeichne das optimierte Bild auf den temporären Canvas
      tempCtx.drawImage(canvas, 0, 0, maxSize, maxSize);
      
      // Warte auf Canvas-Rendering - KRITISCH für korrekte Speicherung
      await waitForNextFrame();
      await wait(50);
      
      console.log('🖼️ Konvertiere zu Base64...');
      // KRITISCHER FIX: Verwende höhere Qualität für bessere Lesbarkeit (0.95 statt 0.8)
      const optimizedPhoto = tempCanvas.toDataURL('image/jpeg', 0.95);
      
      // DEBUG: Log Base64-String Details
      console.log('🔍 [DEBUG] Base64-String Details:', {
        length: optimizedPhoto.length,
        prefix: optimizedPhoto.substring(0, 50),
        suffix: optimizedPhoto.substring(optimizedPhoto.length - 50),
        startsWithData: optimizedPhoto.startsWith('data:image/jpeg;base64,'),
        base64OnlyLength: optimizedPhoto.replace(/^data:image\/[a-z]+;base64,/, '').length
      });
      
      // Validiere das generierte Base64-Bild
      if (!optimizedPhoto || optimizedPhoto.length < 100) {
        throw new Error('Generiertes Base64-Bild ist ungültig oder zu klein');
      }
      
      // Zusätzliche Validierung: Prüfe auf korrupte Base64-Strings
      if (!optimizedPhoto.startsWith('data:image/jpeg;base64,')) {
        throw new Error('Base64-String hat falsches Format');
      }
      
      // Prüfe auf doppelte Base64-Kodierung (häufiges Problem)
      const base64Part = optimizedPhoto.replace(/^data:image\/[a-z]+;base64,/, '');
      try {
        // Versuche zu dekodieren - sollte nicht fehlschlagen
        atob(base64Part.substring(0, 100)); // Teste nur ersten Teil
        console.log('✅ Base64-String ist valide');
      } catch (error) {
        console.error('❌ Base64-String ist korrupt:', error);
        throw new Error('Base64-String ist korrupt und kann nicht dekodiert werden');
      }

      console.log('📤 Sende an Backend...');
      const response = await api.put(`/foto-optimierung/student/${student.sophomorixUnid}`, {
        optimizedPhoto
      });

      console.log('✅ Speicher-Response:', response.data);

      // Aktualisiere lokale Vorschau und Zustand
      updateSelectedStudent(prev => prev ? { ...prev, photo: optimizedPhoto } : prev);
      setStudents(prevStudents =>
        prevStudents.map(studentItem =>
          studentItem.sophomorixUnid === student.sophomorixUnid
            ? { ...studentItem, hasPhoto: true, photoPreview: optimizedPhoto.substring(0, 100) + '...' }
            : studentItem
        )
      );
      setBatchQueue(prevQueue => {
        if (!batchMode) {
          return prevQueue;
        }
        const updatedQueue = prevQueue.map(queueItem =>
          queueItem.sophomorixUnid === student.sophomorixUnid
            ? { ...queueItem, photo: optimizedPhoto }
            : queueItem
        );
        batchQueueRef.current = updatedQueue;
        return updatedQueue;
      });

      // Lade das optimierte Bild erneut, damit weitere Anpassungen auf der neuen Basis erfolgen
      await loadImageForProcessing(optimizedPhoto);
      setBrightness(0);
      setContrast(0);
      setRotation(0);
      setCropRegion(null);
      cropRegionRef.current = null;
      faceBoundsRef.current = null;
      setFaceBounds(null);
      setFaceDetected(false);

      toast.success('Optimiertes Foto erfolgreich gespeichert!', { id: toastId });

    } catch (error) {
      console.error('❌ Fehler beim Speichern:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      toast.error(`Fehler beim Speichern des optimierten Fotos: ${errorMessage}`, { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = student.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.klasse.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClass = !classFilter || student.klasse === classFilter;
    return matchesSearch && matchesClass;
  });

  const availableClasses = [...new Set(students.map(s => s.klasse))].sort();

  // Batch-Modus Funktionen
  const toggleStudentSelection = (sophomorixUnid: string) => {
    setSelectedStudents(prev => 
      prev.includes(sophomorixUnid) 
        ? prev.filter(id => id !== sophomorixUnid)
        : [...prev, sophomorixUnid]
    );
  };

  const selectAllStudents = () => {
    setSelectedStudents(filteredStudents.map(s => s.sophomorixUnid));
  };

  const deselectAllStudents = () => {
    setSelectedStudents([]);
  };

  const startBatchMode = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Schüler aus');
      return;
    }

    try {
      const response = await api.post('/foto-optimierung/batch-optimize', {
        studentIds: selectedStudents
      });

      const batchStudents = response.data.data;
      setBatchQueue(batchStudents);
      batchQueueRef.current = batchStudents;
      setCurrentBatchIndex(0);
      currentBatchIndexRef.current = 0;
      setBatchMode(true);
      fullAutoModeRef.current = false;
      setFullAutoMode(false);
      setBatchProcessing(false);
      setBatchPaused(false);
      updateSelectedStudent(batchStudents[0] || null);
      setBatchResults({success: 0, errors: 0, processed: 0});

      resetProcessingState();

      if (batchStudents[0] && batchStudents[0].photo) {
        try {
          await loadImageForProcessing(batchStudents[0].photo);
          console.log('[Batch-Modus] Erstes Bild erfolgreich geladen');
        } catch (imageError) {
          console.error('[Batch-Modus] Fehler beim Laden des ersten Bildes:', imageError);
          // Fortfahren auch wenn das erste Bild nicht geladen werden kann
          showWarning('Erstes Bild konnte nicht geladen werden, aber Batch-Modus ist gestartet');
        }
      }

      toast.success(`${batchStudents.length} Schüler für Batch-Optimierung geladen`);
    } catch (error) {
      console.error('Fehler beim Starten des Batch-Modus:', error);
      toast.error('Fehler beim Laden der Schüler für Batch-Optimierung');
    }
  };

  const exitBatchMode = () => {
    // Stoppe automatische Verarbeitung
    if (autoProcessingTimeout) {
      clearTimeout(autoProcessingTimeout);
      setAutoProcessingTimeout(null);
    }

    resetProcessingState();
    fullAutoModeRef.current = false;
    
    setBatchMode(false);
    setSelectedStudents([]);
    setBatchQueue([]);
    batchQueueRef.current = [];
    setCurrentBatchIndex(0);
    currentBatchIndexRef.current = 0;
    setBatchProcessing(false);
    setBatchPaused(false);
    setFullAutoMode(false);
    updateSelectedStudent(null);
    setBatchResults({success: 0, errors: 0, processed: 0});
  };

  const nextBatchStudent = async (): Promise<boolean> => {
    // Verwende Refs für aktuelle Werte
    const currentIndex = currentBatchIndexRef.current;
    const queue = batchQueueRef.current;
    
    if (currentIndex >= queue.length - 1) {
      console.log('[nextBatchStudent] Bereits beim letzten Schüler');
      return false;
    }

    const nextIndex = currentIndex + 1;
    const nextStudent = queue[nextIndex] ?? null;

    console.log(`[nextBatchStudent] Wechsle von Index ${currentIndex} zu ${nextIndex}`);
    console.log(`[nextBatchStudent] Neuer Schüler: ${nextStudent?.fullName} (${nextStudent?.sophomorixUnid})`);

    setCurrentBatchIndex(nextIndex);
    currentBatchIndexRef.current = nextIndex;
    updateSelectedStudent(nextStudent);
    selectedStudentRef.current = nextStudent;
    resetProcessingState();

    try {
      if (nextStudent?.photo) {
        await loadImageForProcessing(nextStudent.photo);
        console.log(`[nextBatchStudent] Bild erfolgreich geladen für ${nextStudent.fullName}`);
        return true;
      }

      console.warn(`[nextBatchStudent] Kein Foto für ${nextStudent?.fullName ?? 'unbekannt'} verfügbar`);
      showWarning(`Kein Foto für ${nextStudent?.fullName ?? 'unbekannt'} verfügbar`);
    } catch (error) {
      console.error(`[nextBatchStudent] Fehler beim Laden des Bildes für ${nextStudent?.fullName}:`, error);
      console.warn(`[nextBatchStudent] Überspringe Schüler ${nextStudent?.fullName} aufgrund von Bildladefehler`);
      // Gebe true zurück, damit der Batch weiterläuft, auch wenn das Bild nicht geladen werden konnte
      return true;
    }

    return false;
  };

  const previousBatchStudent = async (): Promise<boolean> => {
    if (currentBatchIndex <= 0) {
      return false;
    }

    const prevIndex = currentBatchIndex - 1;
    const prevStudent = batchQueue[prevIndex] ?? null;

    setCurrentBatchIndex(prevIndex);
    currentBatchIndexRef.current = prevIndex;
    updateSelectedStudent(prevStudent);
    resetProcessingState();

    try {
      if (prevStudent?.photo) {
        await loadImageForProcessing(prevStudent.photo);
        console.log('[Batch-Modus] Vorheriges Bild erfolgreich geladen');
        return true;
      }

      console.warn('[Batch-Modus] Kein Foto für vorherigen Schüler verfügbar');
      showWarning(`Kein Foto für ${prevStudent?.fullName ?? 'unbekannt'} verfügbar`);
    } catch (error) {
      console.error('Fehler beim Laden des vorherigen Bildes:', error);
      toast.error('Fehler beim Laden des vorherigen Bildes');
    }

    return false;
  };

  const saveBatchPhoto = async (
    options: { skipAutoAdvance?: boolean; silent?: boolean } = {}
  ): Promise<boolean> => {
    const student = selectedStudentRef.current;
    if (!student || !canvasRef.current) return false;

    const { skipAutoAdvance = false, silent = false } = options;

    try {
      const canvas = canvasRef.current;
      
      // Stelle sicher, dass das Canvas korrekt gerendert ist
      await waitForNextFrame();
      await wait(100);
      
      const maxSize = 800;
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      
      if (!tempCtx) return false;

      tempCanvas.width = maxSize;
      tempCanvas.height = maxSize;
      
      tempCtx.drawImage(canvas, 0, 0, maxSize, maxSize);
      
      // Warte auf Canvas-Rendering
      await waitForNextFrame();
      await wait(50);
      
      // KRITISCHER FIX: Verwende höhere Qualität für bessere Lesbarkeit (0.95 statt 0.8)
      const optimizedPhoto = tempCanvas.toDataURL('image/jpeg', 0.95);
      
      // Validiere das generierte Base64-Bild
      if (!optimizedPhoto || optimizedPhoto.length < 100) {
        console.error('[saveBatchPhoto] Generiertes Base64-Bild ist ungültig');
        return false;
      }
      
      // Zusätzliche Validierung: Prüfe auf korrupte Base64-Strings
      if (!optimizedPhoto.startsWith('data:image/jpeg;base64,')) {
        console.error('[saveBatchPhoto] Base64-String hat falsches Format');
        return false;
      }
      
      // Prüfe auf doppelte Base64-Kodierung (häufiges Problem)
      const base64Part = optimizedPhoto.replace(/^data:image\/[a-z]+;base64,/, '');
      try {
        // Versuche zu dekodieren - sollte nicht fehlschlagen
        atob(base64Part.substring(0, 100)); // Teste nur ersten Teil
        console.log('[saveBatchPhoto] ✅ Base64-String ist valide');
      } catch (error) {
        console.error('[saveBatchPhoto] ❌ Base64-String ist korrupt:', error);
        return false;
      }

      console.log(`[saveBatchPhoto] Speichere für Schüler: ${student.fullName} (${student.sophomorixUnid})`);
      await api.put(`/foto-optimierung/student/${student.sophomorixUnid}`, {
        optimizedPhoto
      });

      // Aktualisiere selectedStudent mit Validierung der sophomorixUnid
      updateSelectedStudent(prev => {
        if (!prev || prev.sophomorixUnid !== student.sophomorixUnid) {
          console.warn(`[saveBatchPhoto] selectedStudent stimmt nicht überein: ${prev?.sophomorixUnid} vs ${student.sophomorixUnid}`);
          return prev;
        }
        return { ...prev, photo: optimizedPhoto };
      });
      setStudents(prevStudents =>
        prevStudents.map(studentItem =>
          studentItem.sophomorixUnid === student.sophomorixUnid
            ? { ...studentItem, hasPhoto: true, photoPreview: optimizedPhoto.substring(0, 100) + '...' }
            : studentItem
        )
      );
      setBatchQueue(prevQueue => {
        const updatedQueue = prevQueue.map(queueItem =>
          queueItem.sophomorixUnid === student.sophomorixUnid
            ? { ...queueItem, photo: optimizedPhoto }
            : queueItem
        );
        batchQueueRef.current = updatedQueue;
        return updatedQueue;
      });
      setBatchResults(prev => ({...prev, success: prev.success + 1}));
      if (!silent) {
        toast.success(`Foto für ${student.fullName} gespeichert`);
      }
      
      if (!skipAutoAdvance) {
        if (currentBatchIndex < batchQueue.length - 1) {
          setTimeout(() => {
            nextBatchStudent();
          }, 1000);
        } else {
          toast.success('Alle Fotos wurden bearbeitet!');
          exitBatchMode();
        }
      }

      return true;
    } catch (error) {
      console.error('[saveBatchPhoto] Fehler beim Speichern:', error);
      setBatchResults(prev => ({...prev, errors: prev.errors + 1}));
      if (!silent) {
        toast.error(`Fehler beim Speichern für ${student.fullName}`);
      }
      return false;
    }
  };

  const autoOptimizeBatch = async () => {
    const student = selectedStudentRef.current;
    if (!student) return;

    try {
      console.log('[Batch-Modus] Starte Auto-Optimierung für:', student.fullName);

      await autoOptimize();
      await waitForNextFrame();

      try {
        await saveBatchPhoto();
      } catch (saveError) {
        console.error('[Batch-Modus] Fehler beim automatischen Speichern:', saveError);
        toast.error('Fehler beim automatischen Speichern');
      }
    } catch (error) {
      console.error('[Batch-Modus] Fehler bei Auto-Optimierung:', error);
      toast.error('Fehler bei der Auto-Optimierung');
    }
  };

  const skipBatchStudent = () => {
    if (currentBatchIndex < batchQueue.length - 1) {
      nextBatchStudent();
    } else {
      toast.success('Batch-Optimierung abgeschlossen!');
      exitBatchMode();
    }
  };

  // Vollautomatische Verarbeitung
  const startFullAutoProcessing = useCallback(async () => {
    if (fullAutoProcessingRef.current) {
      return fullAutoProcessingRef.current;
    }

    const run = (async () => {
      console.log('[Vollautomatisch] Starte Verarbeitung...');
      
      while (fullAutoModeRef.current) {
        // Aktuelle Werte aus den Refs holen
        const queue = batchQueueRef.current;
        const currentIndex = currentBatchIndexRef.current;
        
        console.log(`[Vollautomatisch] Verarbeite Index ${currentIndex + 1}/${queue.length}`);

        // Prüfe ob Batch leer ist
        if (!queue.length) {
          console.log('[Vollautomatisch] Keine Schüler im Batch');
          toast.success('Kein Schüler im Batch – nichts zu tun');
          setBatchProcessing(false);
          setFullAutoMode(false);
          fullAutoModeRef.current = false;
          exitBatchMode();
          break;
        }

        // Prüfe ob alle Schüler verarbeitet wurden
        if (currentIndex >= queue.length) {
          console.log('[Vollautomatisch] Alle Schüler verarbeitet');
          toast.success('🎉 Vollautomatische Batch-Optimierung abgeschlossen!');
          setBatchProcessing(false);
          setFullAutoMode(false);
          fullAutoModeRef.current = false;
          exitBatchMode();
          break;
        }

        const student = selectedStudentRef.current;
        console.log(`[Vollautomatisch] Verarbeite Schüler: ${student?.fullName} (${student?.sophomorixUnid})`);

        // Prüfe ob Schüler und Foto vorhanden sind
        if (!student || !student.photo) {
          console.warn(`[Vollautomatisch] Kein Foto für ${student?.fullName ?? 'unbekannt'}`);
          setBatchResults(prev => ({ ...prev, errors: prev.errors + 1 }));
          showWarning(`[Vollautomatisch] Kein Foto für ${student?.fullName ?? 'unbekannt'} – überspringe`);

          // Zum nächsten Schüler wechseln
          if (currentIndex < queue.length - 1) {
            console.log('[Vollautomatisch] Wechsle zum nächsten Schüler (kein Foto)');
            const advanced = await nextBatchStudent();
            if (!advanced) {
              console.error('[Vollautomatisch] Wechsel zum nächsten Schüler fehlgeschlagen - keine weiteren Schüler verfügbar');
              setBatchProcessing(false);
              setFullAutoMode(false);
              fullAutoModeRef.current = false;
              showWarning('Automatische Verarbeitung abgeschlossen (keine weiteren Schüler verfügbar)');
              exitBatchMode();
              break;
            } else {
              console.log('[Vollautomatisch] Schüler ohne Foto erfolgreich übersprungen');
            }
            await wait(500); // Längere Pause bei Fehlern
            continue;
          } else {
            // Letzter Schüler ohne Foto
            console.log('[Vollautomatisch] Letzter Schüler ohne Foto - Batch abgeschlossen');
            toast.success('Vollautomatische Batch-Optimierung abgeschlossen (mit Fehlern)');
            setBatchProcessing(false);
            setFullAutoMode(false);
            fullAutoModeRef.current = false;
            exitBatchMode();
            break;
          }
        }

        // Warte auf Bildladen
        console.log(`[Vollautomatisch] Warte auf Bildladen für ${student.fullName}`);
        const ready = await waitForImageReady(8000);
        if (!ready) {
          console.error(`[Vollautomatisch] Bild konnte nicht geladen werden für ${student.fullName}`);
          setBatchResults(prev => ({ ...prev, errors: prev.errors + 1 }));
          toast.error(`[Vollautomatisch] Foto für ${student.fullName} konnte nicht geladen werden – überspringe`);

          // Zum nächsten Schüler wechseln
          if (currentIndex < queue.length - 1) {
            console.log('[Vollautomatisch] Wechsle zum nächsten Schüler (Bildladefehler)');
            const advanced = await nextBatchStudent();
            if (!advanced) {
              console.error('[Vollautomatisch] Wechsel zum nächsten Schüler fehlgeschlagen - keine weiteren Schüler verfügbar');
              setBatchProcessing(false);
              setFullAutoMode(false);
              fullAutoModeRef.current = false;
              showWarning('Automatische Verarbeitung abgeschlossen (keine weiteren Schüler verfügbar)');
              exitBatchMode();
              break;
            } else {
              console.log('[Vollautomatisch] Schüler nach Bildladefehler erfolgreich übersprungen');
            }
            await wait(500);
            continue;
          } else {
            // Letzter Schüler mit Bildladefehler
            console.log('[Vollautomatisch] Letzter Schüler mit Bildladefehler - Batch abgeschlossen');
            toast.success('Vollautomatische Batch-Optimierung abgeschlossen (mit Fehlern)');
            setBatchProcessing(false);
            setFullAutoMode(false);
            fullAutoModeRef.current = false;
            exitBatchMode();
            break;
          }
        }

        // Prüfe ob Auto-Modus noch aktiv ist
        if (!fullAutoModeRef.current) {
          console.log('[Vollautomatisch] Auto-Modus deaktiviert - Verarbeitung gestoppt');
          break;
        }

        // Auto-Optimierung durchführen
        console.log(`[Vollautomatisch] Führe Auto-Optimierung durch für ${student.fullName}`);
        try {
          await autoOptimize();
          await waitForNextFrame();
          await wait(1000); // Zusätzliche Pause für Gesichtserkennung
        } catch (optimizeError) {
          console.error(`[Vollautomatisch] Auto-Optimierung fehlgeschlagen für ${student.fullName}:`, optimizeError);
          setBatchResults(prev => ({ ...prev, errors: prev.errors + 1 }));
        }

        // Prüfe ob Auto-Modus noch aktiv ist
        if (!fullAutoModeRef.current) {
          console.log('[Vollautomatisch] Auto-Modus deaktiviert nach Optimierung');
          break;
        }

        // Foto speichern
        console.log(`[Vollautomatisch] Speichere Foto für ${student.fullName} (${student.sophomorixUnid})`);
        const saved = await saveBatchPhoto({ skipAutoAdvance: true, silent: true });
        if (!saved) {
          console.error(`[Vollautomatisch] Speichern fehlgeschlagen für ${student.fullName}`);
          setBatchResults(prev => ({ ...prev, errors: prev.errors + 1 }));
        } else {
          console.log(`[Vollautomatisch] Foto erfolgreich gespeichert für ${student.fullName}`);
          setBatchResults(prev => ({ ...prev, processed: prev.processed + 1 }));
        }

        // Prüfe ob Auto-Modus noch aktiv ist
        if (!fullAutoModeRef.current) {
          console.log('[Vollautomatisch] Auto-Modus deaktiviert nach Speichern');
          break;
        }

        // Zum nächsten Schüler wechseln
        if (currentIndex < queue.length - 1) {
          console.log(`[Vollautomatisch] Wechsle zum nächsten Schüler (${currentIndex + 1} -> ${currentIndex + 2})`);
          await wait(500); // Pause zwischen Schülern
          
          const advanced = await nextBatchStudent();
          if (!advanced) {
            console.error('[Vollautomatisch] Wechsel zum nächsten Schüler fehlgeschlagen - keine weiteren Schüler verfügbar');
            setBatchProcessing(false);
            setFullAutoMode(false);
            fullAutoModeRef.current = false;
            showWarning('Automatische Verarbeitung abgeschlossen (keine weiteren Schüler verfügbar)');
            exitBatchMode();
            break;
          } else {
            console.log('[Vollautomatisch] Schülerwechsel erfolgreich - setze Verarbeitung fort');
          }
          
          // WICHTIG: Aktualisiere die Refs nach dem Schülerwechsel
          await wait(200); // Kurze Pause für State-Update
          
          // Hole die aktualisierten Werte aus den Refs
          const newIndex = currentBatchIndexRef.current;
          const newQueue = batchQueueRef.current;
          
          console.log(`[Vollautomatisch] Neuer Index: ${newIndex + 1}/${newQueue.length}`);
          console.log(`[Vollautomatisch] Neuer Schüler: ${selectedStudentRef.current?.fullName} (${selectedStudentRef.current?.sophomorixUnid})`);
          
          await wait(500); // Pause nach Schülerwechsel
          continue;
        } else {
          // Letzter Schüler verarbeitet
          console.log('[Vollautomatisch] Letzter Schüler verarbeitet - Batch abgeschlossen');
          toast.success('🎉 Vollautomatische Batch-Optimierung abgeschlossen!');
          setBatchProcessing(false);
          setFullAutoMode(false);
          fullAutoModeRef.current = false;
          exitBatchMode();
          break;
        }
      }
      
      console.log('[Vollautomatisch] Verarbeitung beendet');
    })();

    fullAutoProcessingRef.current = run;

    try {
      await run;
    } catch (error) {
      console.error('[Vollautomatisch] Unerwarteter Fehler:', error);
      toast.error('Vollautomatische Verarbeitung abgebrochen');
      setBatchProcessing(false);
      setFullAutoMode(false);
      fullAutoModeRef.current = false;
    } finally {
      fullAutoProcessingRef.current = null;
      setAutoProcessingTimeout(null);
    }
  }, [autoOptimize, exitBatchMode, nextBatchStudent, saveBatchPhoto, showWarning, wait, waitForImageReady, waitForNextFrame]);

  const startFullAutoMode = () => {
    if (!batchQueue.length) {
      toast.error('Keine Schüler im Batch ausgewählt');
      return;
    }

    if (fullAutoProcessingRef.current) {
      toast.error('Vollautomatische Verarbeitung läuft bereits');
      return;
    }

    setFullAutoMode(true);
    fullAutoModeRef.current = true;
    setBatchProcessing(true);
    toast.success(`🚀 Vollautomatische Batch-Optimierung gestartet für ${batchQueue.length} Schüler`);

    startFullAutoProcessing().catch(error => {
      console.error('[Vollautomatisch] Unerwarteter Fehler:', error);
      toast.error('Vollautomatische Verarbeitung abgebrochen');
      setBatchProcessing(false);
      setFullAutoMode(false);
      fullAutoModeRef.current = false;
    });
  };

  const stopFullAutoMode = () => {
    setFullAutoMode(false);
    fullAutoModeRef.current = false;
    setBatchProcessing(false);
    if (autoProcessingTimeout) {
      clearTimeout(autoProcessingTimeout);
      setAutoProcessingTimeout(null);
    }
    showInfo('Vollautomatische Verarbeitung gestoppt');
  };
  const detectionMethodLabel = lastDetectionMethod === 'faceDetector'
    ? 'FaceDetector API'
    : lastDetectionMethod === 'mediapipe'
      ? 'Mediapipe-Erkennung'
      : lastDetectionMethod === 'fallback'
        ? 'Fallback-Heuristik'
        : lastDetectionMethod === 'none'
          ? 'Keine Erkennung'
          : 'Noch keine Erkennung';
  const fallbackScoreLabel = lastFallbackScore != null ? lastFallbackScore.toFixed(2) : '–';

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Zugriff verweigert</h1>
          <p className="text-gray-600">Nur Administratoren können auf die Foto-Optimierung zugreifen.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-16 w-16 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Lade Daten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Camera className="h-8 w-8 text-blue-600" />
                Foto-Optimierung
              </h1>
              <p className="mt-2 text-gray-600">
                Verwende Gesichtserkennung und Auto-Crop für optimale Schülerfotos
              </p>
            </div>
            
            {stats && (
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">{stats.photoPercentage}%</div>
                <div className="text-sm text-gray-500">
                  {stats.studentsWithPhotos} von {stats.totalStudents} Schülern haben Fotos
                </div>
              </div>
            )}
          </div>
        </div>

        {selectedStudent ? (
          /* Foto-Bearbeitung */
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {selectedStudent.fullName}
                  </h2>
                  <p className="text-sm text-gray-500">
                    Klasse: {selectedStudent.klasse}
                    {batchMode && (
                      <span className="ml-4 text-blue-600 font-medium">
                        {currentBatchIndex + 1} von {batchQueue.length}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {batchMode && (
                    <div className="flex items-center gap-2 mr-4">
                      <button
                        onClick={previousBatchStudent}
                        disabled={currentBatchIndex === 0}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                      >
                        <SkipForward className="h-4 w-4 rotate-180" />
                      </button>
                      <span className="text-sm text-gray-500">
                        {currentBatchIndex + 1}/{batchQueue.length}
                      </span>
                      <button
                        onClick={nextBatchStudent}
                        disabled={currentBatchIndex === batchQueue.length - 1}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                      >
                        <SkipForward className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  <button
                    onClick={batchMode ? exitBatchMode : () => updateSelectedStudent(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <RotateCcw className="h-6 w-6" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Bildvorschau */}
                <div className="space-y-4">
                  <div className="relative">
                    <canvas
                      ref={canvasRef}
                      className="border border-gray-300 rounded-lg mx-auto block"
                      style={{ maxWidth: '100%', height: 'auto' }}
                    />
                    
                    {/* Loading-Indikator */}
                    {!imageLoaded && (
                      <div className="absolute inset-0 bg-gray-100 rounded-lg flex items-center justify-center">
                        <div className="text-center">
                          <Loader2 className="h-8 w-8 text-blue-500 animate-spin mx-auto mb-2" />
                          <p className="text-sm text-gray-600">Lade Bild...</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Gesichtserkennungs-Indikator */}
                    {faceDetected && faceBounds && (
                      <div className="absolute top-2 left-2 bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                        <CheckCircle className="h-4 w-4 inline mr-1" />
                        Gesicht erkannt
                      </div>
                    )}
                    
                    {/* Bild-Status-Indikator */}
                    {imageLoaded && (
                      <div className="absolute top-2 right-2 bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                        <CheckCircle className="h-4 w-4 inline mr-1" />
                        Bild geladen
                      </div>
                    )}
                  </div>

                  {/* Steuerung */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Helligkeit: {brightness}
                      </label>
                      <input
                        type="range"
                        min="-100"
                        max="100"
                        value={brightness}
                        onChange={(e) => {
                          setBrightness(Number(e.target.value));
                        }}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Kontrast: {contrast}
                      </label>
                      <input
                        type="range"
                        min="-100"
                        max="100"
                        value={contrast}
                        onChange={(e) => {
                          setContrast(Number(e.target.value));
                        }}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rotation: {rotation}°
                      </label>
                      <input
                        type="range"
                        min="-180"
                        max="180"
                        value={rotation}
                        onChange={(e) => {
                          setRotation(Number(e.target.value));
                        }}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                {/* Aktionen */}
                <div className="space-y-4">
                  {/* Batch-Modus Status */}
                  {batchMode && (
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-medium text-blue-900">Batch-Optimierung</h3>
                        {fullAutoMode && (
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="text-sm text-green-700 font-medium">Vollautomatisch</span>
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Fortschritt:</span>
                          <span className="font-medium">{currentBatchIndex + 1} von {batchQueue.length}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Erfolgreich:</span>
                          <span className="text-green-600 font-medium">{batchResults.success}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Fehler:</span>
                          <span className="text-red-600 font-medium">{batchResults.errors}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${((currentBatchIndex + 1) / batchQueue.length) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Automatische Optimierung</h3>
                    
                    <div className="space-y-3">
                      {batchMode && !fullAutoMode && (
                        <button
                          onClick={startFullAutoMode}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <Play className="h-4 w-4" />
                          Vollautomatisch starten
                        </button>
                      )}
                      
                      {batchMode && fullAutoMode && (
                        <button
                          onClick={stopFullAutoMode}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          <Pause className="h-4 w-4" />
                          Vollautomatisch stoppen
                        </button>
                      )}
                      
                      <button
                        onClick={batchMode ? autoOptimizeBatch : autoOptimize}
                        disabled={fullAutoMode}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Sun className="h-4 w-4" />
                        {batchMode ? 'Auto-Optimieren & Speichern' : 'Auto-Optimieren'}
                      </button>

                      {faceDetected && (
                        <button
                          onClick={autoCropToFace}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <ZoomIn className="h-4 w-4" />
                          Auto-Crop (Gesicht)
                        </button>
                      )}

                      <button
                        onClick={resetImage}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        <RotateCcw className="h-4 w-4" />
                        Zurücksetzen
                      </button>
                    </div>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Speichern</h3>
                    
                    <div className="space-y-3">
                      <button
                        onClick={batchMode ? () => saveBatchPhoto() : saveOptimizedPhoto}
                        disabled={saving}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                      >
                        {saving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        {saving ? 'Speichern...' : batchMode ? 'Speichern & Weiter' : 'Optimiertes Foto speichern'}
                      </button>

                      {batchMode && (
                        <button
                          onClick={skipBatchStudent}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        >
                          <SkipForward className="h-4 w-4" />
                          Überspringen & Weiter
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="text-lg font-medium text-purple-900 mb-3">Debugging</h3>
                    <div className="space-y-3">
                      <button
                        onClick={() => setShowDebugOverlay(prev => !prev)}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${showDebugOverlay ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-50'}`}
                      >
                        <Bug className="h-4 w-4" />
                        {showDebugOverlay ? 'Debug-Overlay ausblenden' : 'Debug-Overlay anzeigen'}
                      </button>

                      <div className="bg-white border border-purple-200 rounded-lg px-3 py-2 text-sm text-purple-800">
                        <p>Letzte Erkennung: {detectionMethodLabel}</p>
                        <p>Fallback-Score: {fallbackScoreLabel}</p>
                        <p>Heatmap-Fenster: {heatmapData.length}</p>
                      </div>
                    </div>
                  </div>

                  {faceDetected && (
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 text-green-800">
                        <CheckCircle className="h-5 w-5" />
                        <span className="font-medium">Gesicht erfolgreich erkannt!</span>
                      </div>
                      <p className="text-sm text-green-700 mt-1">
                        Das Gesicht wurde erkannt und kann automatisch zugeschnitten werden.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Schülerliste */
          <div className="space-y-6">
            {/* Filter und Batch-Auswahl */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Suche
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Name oder Klasse suchen..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Klasse filtern
                  </label>
                  <select
                    value={classFilter}
                    onChange={(e) => setClassFilter(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Alle Klassen</option>
                    {availableClasses.map(klasse => (
                      <option key={klasse} value={klasse}>{klasse}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Batch-Auswahl Controls */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <h3 className="text-lg font-medium text-gray-900">Batch-Optimierung</h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={selectAllStudents}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <CheckSquare className="h-4 w-4" />
                        Alle auswählen
                      </button>
                      <button
                        onClick={deselectAllStudents}
                        className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
                      >
                        <Square className="h-4 w-4" />
                        Alle abwählen
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">
                      {selectedStudents.length} von {filteredStudents.length} ausgewählt
                    </span>
                    <button
                      onClick={startBatchMode}
                      disabled={selectedStudents.length === 0}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Play className="h-4 w-4" />
                      Batch starten ({selectedStudents.length})
                    </button>
                    <button
                      onClick={async () => {
                        await startBatchMode();
                        setTimeout(() => {
                          if (batchQueue.length > 0) {
                            startFullAutoMode();
                          }
                        }, 1000);
                      }}
                      disabled={selectedStudents.length === 0}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Play className="h-4 w-4" />
                      Vollautomatisch ({selectedStudents.length})
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Schülerliste */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  Schüler mit Fotos ({filteredStudents.length})
                </h2>
              </div>
              
              <div className="divide-y divide-gray-200">
                {filteredStudents.map((student, index) => {
                  const isSelected = selectedStudents.includes(student.sophomorixUnid);
                  return (
                    <div
                      key={student.id || student.sophomorixUnid || `student-${index}`}
                      className={`px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                        isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      }`}
                      onClick={() => toggleStudentSelection(student.sophomorixUnid)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {/* Auswahl-Checkbox */}
                          <div className="flex-shrink-0">
                            {isSelected ? (
                              <CheckSquare className="h-5 w-5 text-blue-600" />
                            ) : (
                              <Square className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                          
                          <div className="h-12 w-12 bg-gray-200 rounded-full flex items-center justify-center">
                            <User className="h-6 w-6 text-gray-500" />
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-gray-900">
                              {student.fullName}
                            </h3>
                            <p className="text-sm text-gray-500">
                              Klasse: {student.klasse} • ID: {student.sophomorixUnid}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1 text-green-600">
                            <Camera className="h-4 w-4" />
                            <span className="text-sm">Foto vorhanden</span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              loadStudentPhoto(student.sophomorixUnid);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Bearbeiten
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
